import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    ShieldCheck,
    Users,
    Database,
    Key,
    Plus,
    Trash,
    Pencil,
    Check,
    X,
    Eye,
    EyeSlash,
    Table as TableIcon,
    Robot,
    CalendarBlank
} from "@phosphor-icons/react";

const FIELD_TYPES = [
    { value: "string", label: "Texto corto" },
    { value: "text", label: "Texto largo" },
    { value: "number", label: "Número" },
    { value: "date", label: "Fecha" },
    { value: "select", label: "Selección" },
    { value: "boolean", label: "Sí/No" },
    { value: "reference", label: "Referencia" },
    { value: "array", label: "Lista" },
];

const LLM_PROVIDERS = [
    { value: "openai", label: "OpenAI", models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"] },
    { value: "anthropic", label: "Anthropic Claude", models: ["claude-3-5-sonnet-20241022", "claude-3-opus-20240229", "claude-3-haiku-20240307"] },
    { value: "google", label: "Google Gemini", models: ["gemini-1.5-pro", "gemini-1.5-flash", "gemini-pro"] },
    { value: "whisper", label: "OpenAI Whisper (Transcripción)", models: ["whisper-1"] },
];

const AdminPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("api-keys");
    
    // API Keys
    const [apiKeys, setApiKeys] = useState([]);
    const [newApiKey, setNewApiKey] = useState({ provider: "", api_key: "", model: "", is_default: true, description: "" });
    const [showKey, setShowKey] = useState({});
    
    // Collections
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [collectionData, setCollectionData] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [newField, setNewField] = useState({ name: "", type: "string", required: false, label: "", options: "", default: "", ref_collection: "" });
    
    // New Collection Dialog
    const [newCollectionOpen, setNewCollectionOpen] = useState(false);
    const [newCollection, setNewCollection] = useState({ 
        name: "", 
        display_name: "", 
        description: "", 
        fields: [],
        show_in_menu: true,
        menu_name: "",
        menu_icon: "database",
        menu_order: 99
    });
    
    // Data Dialog
    const [dataDialogOpen, setDataDialogOpen] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [dataForm, setDataForm] = useState({});
    
    // Users
    const [users, setUsers] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [editUserOpen, setEditUserOpen] = useState(false);
    const [newUserOpen, setNewUserOpen] = useState(false);
    const [newUser, setNewUser] = useState({ email: "", password: "", name: "", company: "", role: "user" });

    // Festivos (Salamanca) — el bot no envía mensajes en fin de semana ni festivos
    const currentYear = new Date().getFullYear();
    const [holidayYear, setHolidayYear] = useState(currentYear);
    const [holidays, setHolidays] = useState([]);
    const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });

    useEffect(() => {
        if (user?.role === "admin") {
            fetchData();
        }
    }, [user]);

    useEffect(() => {
        if (user?.role === "admin") {
            fetchHolidays(holidayYear);
        }
    }, [user, holidayYear]);

    const fetchHolidays = async (year) => {
        try {
            const res = await axios.get(`${API}/holidays`, { params: { year } });
            setHolidays(res.data.holidays || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddHoliday = async () => {
        if (!newHoliday.date) {
            toast.error("Indica la fecha del festivo");
            return;
        }
        try {
            await axios.post(`${API}/holidays/custom`, {
                date: newHoliday.date,
                name: newHoliday.name || "Festivo"
            });
            toast.success("Festivo añadido");
            setNewHoliday({ date: "", name: "" });
            fetchHolidays(holidayYear);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Error al añadir el festivo");
        }
    };

    const handleDeleteHoliday = async (dateStr) => {
        if (!window.confirm("¿Quitar este festivo personalizado?")) return;
        try {
            await axios.delete(`${API}/holidays/custom/${dateStr}`);
            toast.success("Festivo eliminado");
            fetchHolidays(holidayYear);
        } catch (error) {
            toast.error("Error al eliminar el festivo");
        }
    };

    const formatHolidayDate = (iso) => {
        const [y, m, d] = iso.split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString("es-ES", {
            weekday: "long", day: "numeric", month: "long"
        });
    };

    const fetchData = async () => {
        try {
            const [keysRes, collectionsRes, usersRes] = await Promise.all([
                axios.get(`${API}/admin/api-keys`),
                axios.get(`${API}/admin/collections`),
                axios.get(`${API}/admin/users`)
            ]);
            setApiKeys(keysRes.data);
            setCollections(collectionsRes.data);
            setUsers(usersRes.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // API Keys handlers
    const handleSaveApiKey = async () => {
        if (!newApiKey.provider || !newApiKey.api_key) {
            toast.error("Proveedor y API Key son requeridos");
            return;
        }
        try {
            await axios.post(`${API}/admin/api-keys`, newApiKey);
            toast.success("API Key guardada");
            setNewApiKey({ provider: "", api_key: "", model: "", is_default: true, description: "" });
            fetchData();
        } catch (error) {
            toast.error("Error al guardar");
        }
    };

    const handleDeleteApiKey = async (provider, model) => {
        if (!window.confirm("¿Eliminar esta API Key?")) return;
        try {
            await axios.delete(`${API}/admin/api-keys/${provider}/${model || ""}`);
            toast.success("API Key eliminada");
            fetchData();
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    // Collections handlers
    const handleSelectCollection = async (collection) => {
        setSelectedCollection(collection);
        try {
            const response = await axios.get(`${API}/admin/collections/${collection.name}/data`);
            setCollectionData(response.data);
        } catch (error) {
            toast.error("Error al cargar datos");
        }
    };

    const handleCreateCollection = async () => {
        if (!newCollection.name) {
            toast.error("El nombre es requerido");
            return;
        }
        try {
            await axios.post(`${API}/admin/collections`, {
                ...newCollection,
                name: newCollection.name.toLowerCase().replace(/\s/g, "_"),
                fields: newCollection.fields.length > 0 ? newCollection.fields : [
                    { name: "name", type: "string", required: true, label: "Nombre" }
                ]
            });
            toast.success("Colección creada");
            setNewCollectionOpen(false);
            setNewCollection({ name: "", display_name: "", description: "", fields: [], show_in_menu: true, menu_name: "", menu_icon: "database", menu_order: 99 });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al crear");
        }
    };

    const handleDeleteCollection = async (name) => {
        if (!window.confirm(`¿Eliminar la colección "${name}" y todos sus datos?`)) return;
        try {
            await axios.delete(`${API}/admin/collections/${name}?delete_data=true`);
            toast.success("Colección eliminada");
            setSelectedCollection(null);
            setCollectionData(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al eliminar");
        }
    };

    const handleUpdateCollectionFields = async () => {
        if (!selectedCollection) return;
        try {
            await axios.put(`${API}/admin/collections/${selectedCollection.name}`, {
                fields: selectedCollection.fields
            });
            toast.success("Campos actualizados");
            fetchData();
        } catch (error) {
            toast.error("Error al actualizar");
        }
    };

    const handleAddField = () => {
        if (!newField.name || !newField.type) return;
        if (newField.type === "reference" && !newField.ref_collection) {
            toast.error("Selecciona una tabla de referencia");
            return;
        }
        const field = {
            ...newField,
            options: newField.type === "select" ? newField.options.split(",").map(o => o.trim()).filter(Boolean) : undefined,
            ref_collection: newField.type === "reference" ? newField.ref_collection : undefined,
        };
        setSelectedCollection({
            ...selectedCollection,
            fields: [...selectedCollection.fields, field]
        });
        setNewField({ name: "", type: "string", required: false, label: "", options: "", default: "", ref_collection: "" });
    };

    const handleRemoveField = (index) => {
        setSelectedCollection({
            ...selectedCollection,
            fields: selectedCollection.fields.filter((_, i) => i !== index)
        });
    };

    // Data handlers
    const handleCreateData = async () => {
        if (!selectedCollection) return;
        try {
            await axios.post(`${API}/admin/collections/${selectedCollection.name}/data`, { data: dataForm });
            toast.success("Registro creado");
            setDataDialogOpen(false);
            setDataForm({});
            handleSelectCollection(selectedCollection);
        } catch (error) {
            toast.error("Error al crear");
        }
    };

    const handleUpdateData = async () => {
        if (!selectedCollection || !editingData) return;
        try {
            await axios.put(`${API}/admin/collections/${selectedCollection.name}/data/${editingData.id}`, { data: dataForm });
            toast.success("Registro actualizado");
            setDataDialogOpen(false);
            setEditingData(null);
            setDataForm({});
            handleSelectCollection(selectedCollection);
        } catch (error) {
            toast.error("Error al actualizar");
        }
    };

    const handleDeleteData = async (itemId) => {
        if (!window.confirm("¿Eliminar este registro?")) return;
        try {
            await axios.delete(`${API}/admin/collections/${selectedCollection.name}/data/${itemId}`);
            toast.success("Registro eliminado");
            handleSelectCollection(selectedCollection);
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    const openEditData = (item) => {
        setEditingData(item);
        setDataForm({ ...item });
        setDataDialogOpen(true);
    };

    // Users handlers
    const handleCreateUser = async () => {
        if (!newUser.email || !newUser.password || !newUser.name) {
            toast.error("Email, contraseña y nombre son requeridos");
            return;
        }
        try {
            await axios.post(`${API}/admin/users?role=${newUser.role}`, {
                email: newUser.email,
                password: newUser.password,
                name: newUser.name,
                company: newUser.company
            });
            toast.success("Usuario creado correctamente");
            setNewUserOpen(false);
            setNewUser({ email: "", password: "", name: "", company: "", role: "user" });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al crear usuario");
        }
    };

    const handleUpdateUser = async () => {
        try {
            await axios.put(`${API}/admin/users/${editingUser.id}`, {
                name: editingUser.name,
                company: editingUser.company || "",
                role: editingUser.role
            });
            toast.success("Usuario actualizado");
            setEditUserOpen(false);
            fetchData();
        } catch (error) {
            toast.error("Error al actualizar");
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("¿Eliminar este usuario?")) return;
        try {
            await axios.delete(`${API}/admin/users/${userId}`);
            toast.success("Usuario eliminado");
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al eliminar");
        }
    };

    if (user?.role !== "admin") {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh]" data-testid="admin-access-denied">
                <ShieldCheck size={64} className="text-red-500 mb-4" />
                <h1 className="font-heading font-bold text-2xl mb-2">Acceso Denegado</h1>
                <p className="text-[#555555]">Solo administradores.</p>
            </div>
        );
    }

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
    }

    return (
        <div className="space-y-6 animate-in" data-testid="admin-page">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-500 rounded-sm flex items-center justify-center">
                    <ShieldCheck size={28} weight="fill" className="text-white" />
                </div>
                <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl">Administración Avanzada</h1>
                    <p className="text-[#555555]">API Keys, Tablas y Usuarios</p>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full max-w-2xl bg-[#F8F9FA] p-1 rounded-sm">
                    <TabsTrigger value="api-keys" className="data-[state=active]:bg-white rounded-sm">
                        <Key size={18} className="mr-2" /> APIs
                    </TabsTrigger>
                    <TabsTrigger value="collections" className="data-[state=active]:bg-white rounded-sm">
                        <TableIcon size={18} className="mr-2" /> Tablas
                    </TabsTrigger>
                    <TabsTrigger value="users" className="data-[state=active]:bg-white rounded-sm">
                        <Users size={18} className="mr-2" /> Usuarios
                    </TabsTrigger>
                    <TabsTrigger value="holidays" className="data-[state=active]:bg-white rounded-sm">
                        <CalendarBlank size={18} className="mr-2" /> Festivos
                    </TabsTrigger>
                </TabsList>

                {/* API KEYS TAB */}
                <TabsContent value="api-keys" className="mt-6 space-y-6">
                    <div className="bg-white border border-[#E5E5E5] rounded-sm p-6">
                        <h2 className="font-heading font-bold mb-4">Añadir API Key</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <Label className="label-overline mb-2 block">Proveedor</Label>
                                <Select value={newApiKey.provider} onValueChange={(v) => setNewApiKey({ ...newApiKey, provider: v, model: "" })}>
                                    <SelectTrigger className="input-industrial">
                                        <SelectValue placeholder="Selecciona" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LLM_PROVIDERS.map(p => (
                                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">Modelo</Label>
                                <Select value={newApiKey.model} onValueChange={(v) => setNewApiKey({ ...newApiKey, model: v })}>
                                    <SelectTrigger className="input-industrial">
                                        <SelectValue placeholder="Selecciona" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LLM_PROVIDERS.find(p => p.value === newApiKey.provider)?.models.map(m => (
                                            <SelectItem key={m} value={m}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">API Key</Label>
                                <Input
                                    type="password"
                                    value={newApiKey.api_key}
                                    onChange={(e) => setNewApiKey({ ...newApiKey, api_key: e.target.value })}
                                    className="input-industrial"
                                    placeholder="sk-..."
                                />
                            </div>
                            <div className="flex items-end">
                                <Button onClick={handleSaveApiKey} className="btn-primary w-full">
                                    <Plus size={18} className="mr-2" /> Guardar
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-[#E5E5E5] rounded-sm">
                        <div className="p-4 border-b border-[#E5E5E5]">
                            <h2 className="font-heading font-bold">API Keys Configuradas</h2>
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow className="table-industrial">
                                    <TableHead>Proveedor</TableHead>
                                    <TableHead>Modelo</TableHead>
                                    <TableHead>API Key</TableHead>
                                    <TableHead>Por defecto</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {apiKeys.length === 0 ? (
                                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#555555]">No hay API keys configuradas</TableCell></TableRow>
                                ) : apiKeys.map((key, i) => (
                                    <TableRow key={i} className="table-industrial">
                                        <TableCell className="font-medium">{LLM_PROVIDERS.find(p => p.value === key.provider)?.label || key.provider}</TableCell>
                                        <TableCell>{key.model || "-"}</TableCell>
                                        <TableCell><code className="text-xs bg-[#F8F9FA] px-2 py-1 rounded">{key.api_key_preview}</code></TableCell>
                                        <TableCell>{key.is_default ? <Check size={18} className="text-green-500" /> : <X size={18} className="text-[#CCC]" />}</TableCell>
                                        <TableCell className="text-right">
                                            <button onClick={() => handleDeleteApiKey(key.provider, key.model)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm">
                                                <Trash size={16} />
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
                        <div className="flex items-start gap-3">
                            <Robot size={24} className="text-blue-600 mt-1" />
                            <div>
                                <h3 className="font-semibold text-blue-800">Bot Inteligente de Telegram y WhatsApp</h3>
                                <p className="text-sm text-blue-700 mt-1">
                                    El bot usará la API key configurada como "por defecto" para cada proveedor. 
                                    Prioridad: OpenAI → Anthropic → Google. 
                                    Para transcripción de audio usa Whisper (OpenAI).
                                </p>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                {/* COLLECTIONS TAB */}
                <TabsContent value="collections" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Collections List */}
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h2 className="font-heading font-bold">Tablas</h2>
                                <Button size="sm" onClick={() => setNewCollectionOpen(true)} className="btn-secondary">
                                    <Plus size={16} className="mr-1" /> Nueva
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {collections.map(col => (
                                    <button
                                        key={col.name}
                                        onClick={() => handleSelectCollection(col)}
                                        className={`w-full text-left p-3 rounded-sm border transition-colors ${
                                            selectedCollection?.name === col.name 
                                                ? "border-[#FFD700] bg-[#FFD700]/10" 
                                                : "border-[#E5E5E5] hover:border-[#0055FF]"
                                        }`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium">{col.display_name || col.name}</span>
                                            <span className="text-xs bg-[#F8F9FA] px-2 py-1 rounded">{col.count || 0}</span>
                                        </div>
                                        {col.is_system && <span className="text-xs text-[#999]">Sistema</span>}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Collection Detail */}
                        <div className="lg:col-span-2">
                            {selectedCollection ? (
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h2 className="font-heading font-bold text-xl">{selectedCollection.display_name || selectedCollection.name}</h2>
                                            <p className="text-sm text-[#555555]">{selectedCollection.description}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            {!selectedCollection.is_system && (
                                                <Button size="sm" variant="destructive" onClick={() => handleDeleteCollection(selectedCollection.name)}>
                                                    <Trash size={16} className="mr-1" /> Eliminar tabla
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Fields */}
                                    <div className="bg-white border border-[#E5E5E5] rounded-sm p-4">
                                        <h3 className="font-semibold mb-3">Campos</h3>
                                        <div className="space-y-2 mb-4">
                                            {selectedCollection.fields?.map((field, i) => (
                                                <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#F8F9FA] rounded-sm">
                                                    <div className="flex items-center gap-3 flex-wrap">
                                                        <span className="font-medium">{field.label || field.name}</span>
                                                        <code className="text-xs bg-white px-2 py-0.5 rounded">{field.name}</code>
                                                        <span className="text-xs text-[#555555]">{FIELD_TYPES.find(t => t.value === field.type)?.label}</span>
                                                        {field.required && <span className="text-xs text-red-500">*</span>}
                                                        {field.type === "reference" && field.ref_collection && (
                                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                                                → {collections.find(c => c.name === field.ref_collection)?.display_name || field.ref_collection}
                                                            </span>
                                                        )}
                                                        {field.type === "select" && field.options?.length > 0 && (
                                                            <span className="text-xs text-[#555555]">
                                                                ({field.options.slice(0, 3).join(", ")}{field.options.length > 3 ? "..." : ""})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button onClick={() => handleRemoveField(i)} className="p-1 hover:bg-red-100 text-red-600 rounded">
                                                        <Trash size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        <div className="grid grid-cols-4 gap-2 items-end">
                                            <Input placeholder="nombre_campo" value={newField.name} onChange={(e) => setNewField({ ...newField, name: e.target.value })} className="input-industrial text-sm" />
                                            <Select value={newField.type} onValueChange={(v) => setNewField({ ...newField, type: v, ref_collection: "" })}>
                                                <SelectTrigger className="input-industrial text-sm"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                            <Input placeholder="Etiqueta" value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} className="input-industrial text-sm" />
                                            <Button size="sm" onClick={handleAddField}><Plus size={16} /></Button>
                                        </div>
                                        {newField.type === "select" && (
                                            <Input placeholder="Opciones separadas por coma" value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} className="input-industrial text-sm mt-2" />
                                        )}
                                        {newField.type === "reference" && (
                                            <div className="mt-2">
                                                <Label className="text-xs text-[#555555] mb-1 block">Tabla relacionada</Label>
                                                <Select value={newField.ref_collection} onValueChange={(v) => setNewField({ ...newField, ref_collection: v })}>
                                                    <SelectTrigger className="input-industrial text-sm">
                                                        <SelectValue placeholder="Selecciona tabla..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {collections
                                                            .filter(c => c.name !== selectedCollection?.name)
                                                            .map(c => (
                                                                <SelectItem key={c.name} value={c.name}>
                                                                    {c.display_name || c.name}
                                                                </SelectItem>
                                                            ))
                                                        }
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        <Button size="sm" onClick={handleUpdateCollectionFields} className="mt-3 btn-primary">
                                            Guardar campos
                                        </Button>
                                    </div>

                                    {/* Data */}
                                    <div className="bg-white border border-[#E5E5E5] rounded-sm">
                                        <div className="p-4 border-b border-[#E5E5E5] flex justify-between items-center">
                                            <h3 className="font-semibold">Datos ({collectionData?.total || 0})</h3>
                                            <Button size="sm" onClick={() => { setEditingData(null); setDataForm({}); setDataDialogOpen(true); }} className="btn-secondary">
                                                <Plus size={16} className="mr-1" /> Añadir
                                            </Button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="table-industrial">
                                                        {selectedCollection.fields?.slice(0, 4).map(f => (
                                                            <TableHead key={f.name}>{f.label || f.name}</TableHead>
                                                        ))}
                                                        <TableHead className="text-right">Acciones</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {collectionData?.data?.length === 0 ? (
                                                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#555555]">Sin datos</TableCell></TableRow>
                                                    ) : collectionData?.data?.map(item => (
                                                        <TableRow key={item.id} className="table-industrial">
                                                            {selectedCollection.fields?.slice(0, 4).map(f => (
                                                                <TableCell key={f.name} className="max-w-[150px] truncate">
                                                                    {String(item[f.name] || "-")}
                                                                </TableCell>
                                                            ))}
                                                            <TableCell className="text-right">
                                                                <button onClick={() => openEditData(item)} className="p-2 hover:bg-[#F8F9FA] rounded-sm"><Pencil size={14} /></button>
                                                                <button onClick={() => handleDeleteData(item.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm"><Trash size={14} /></button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-64 text-[#555555]">
                                    <div className="text-center">
                                        <Database size={48} className="mx-auto mb-4 text-[#CCC]" />
                                        <p>Selecciona una tabla para ver sus datos</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* USERS TAB */}
                <TabsContent value="users" className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="font-heading font-bold text-xl">Gestión de Usuarios</h2>
                        <Button onClick={() => setNewUserOpen(true)} className="btn-primary">
                            <Plus size={18} className="mr-2" /> Nuevo Usuario
                        </Button>
                    </div>
                    <div className="bg-white border border-[#E5E5E5] rounded-sm">
                        <Table>
                            <TableHeader>
                                <TableRow className="table-industrial">
                                    <TableHead>Usuario</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Empresa</TableHead>
                                    <TableHead>Rol</TableHead>
                                    <TableHead>Telegram</TableHead>
                                    <TableHead>WhatsApp</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map(u => (
                                    <TableRow key={u.id} className="table-industrial">
                                        <TableCell className="font-medium">{u.name}</TableCell>
                                        <TableCell>{u.email}</TableCell>
                                        <TableCell>{u.company || "-"}</TableCell>
                                        <TableCell>
                                            <span className={`text-xs font-bold px-2 py-1 rounded-sm ${u.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                                {u.role === 'admin' ? 'Administrador' : 'Usuario'}
                                            </span>
                                        </TableCell>
                                        <TableCell>{u.telegram_chat_id ? <Check size={18} className="text-green-500" /> : <X size={18} className="text-[#CCC]" />}</TableCell>
                                        <TableCell>{u.whatsapp_id ? <Check size={18} className="text-green-500" /> : <X size={18} className="text-[#CCC]" />}</TableCell>
                                        <TableCell className="text-right">
                                            <button onClick={() => { setEditingUser(u); setEditUserOpen(true); }} className="p-2 hover:bg-[#F8F9FA] rounded-sm"><Pencil size={16} /></button>
                                            <button onClick={() => handleDeleteUser(u.id)} disabled={u.id === user.id} className="p-2 hover:bg-red-50 text-red-600 rounded-sm disabled:opacity-30"><Trash size={16} /></button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </TabsContent>

                {/* FESTIVOS TAB */}
                <TabsContent value="holidays" className="mt-6 space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-sm text-blue-900">
                        El bot <strong>no envía mensajes automáticos</strong> (recordatorios de fotos, resumen diario ni recordatorios personalizados) en <strong>sábado, domingo</strong> ni en los <strong>festivos</strong> de esta lista. Sí seguirá respondiendo a quien le escriba. Zona horaria: Europe/Madrid (Salamanca).
                    </div>

                    <div className="bg-white border border-[#E5E5E5] rounded-sm p-6">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <h2 className="font-heading font-bold text-xl">Festivos de {holidayYear} · Salamanca</h2>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => setHolidayYear(holidayYear - 1)}>◀</Button>
                                <span className="font-bold w-14 text-center">{holidayYear}</span>
                                <Button variant="outline" size="sm" onClick={() => setHolidayYear(holidayYear + 1)}>▶</Button>
                            </div>
                        </div>

                        <div className="bg-white border border-[#E5E5E5] rounded-sm">
                            <Table>
                                <TableHeader>
                                    <TableRow className="table-industrial">
                                        <TableHead>Fecha</TableHead>
                                        <TableHead>Festivo</TableHead>
                                        <TableHead>Origen</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {holidays.length === 0 ? (
                                        <TableRow><TableCell colSpan={4} className="text-center text-[#999] py-6">No hay festivos para este año.</TableCell></TableRow>
                                    ) : holidays.map(h => (
                                        <TableRow key={h.date} className="table-industrial">
                                            <TableCell className="font-mono text-sm whitespace-nowrap">{h.date}</TableCell>
                                            <TableCell className="font-medium capitalize">{h.name}<span className="block text-xs text-[#999] font-normal capitalize">{formatHolidayDate(h.date)}</span></TableCell>
                                            <TableCell>
                                                <span className={`text-xs font-bold px-2 py-1 rounded-sm ${h.source === 'custom' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                                                    {h.source === 'custom' ? 'Personalizado' : 'Oficial'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {h.source === 'custom' ? (
                                                    <button onClick={() => handleDeleteHoliday(h.date)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm"><Trash size={16} /></button>
                                                ) : (
                                                    <span className="text-xs text-[#CCC]">—</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        <p className="text-xs text-[#999] mt-3">
                            Los festivos <strong>oficiales</strong> (nacionales, Castilla y León y locales de Salamanca) se calculan automáticamente cada año, incluidos los móviles (Semana Santa y Lunes de Aguas). Añade abajo cualquier festivo local que cambie en el calendario laboral.
                        </p>
                    </div>

                    <div className="bg-white border border-[#E5E5E5] rounded-sm p-6">
                        <h3 className="font-heading font-bold mb-4">Añadir festivo personalizado</h3>
                        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-4 items-end">
                            <div>
                                <Label className="label-overline mb-2 block">Fecha</Label>
                                <Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} className="input-industrial" />
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">Nombre (opcional)</Label>
                                <Input value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} className="input-industrial" placeholder="Ej: Feria local" />
                            </div>
                            <Button onClick={handleAddHoliday} className="btn-primary">
                                <Plus size={18} className="mr-2" /> Añadir
                            </Button>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* New Collection Dialog */}
            <Dialog open={newCollectionOpen} onOpenChange={setNewCollectionOpen}>
                <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Nueva Tabla</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="label-overline mb-2 block">Nombre técnico</Label>
                            <Input value={newCollection.name} onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })} className="input-industrial" placeholder="mi_tabla" />
                        </div>
                        <div>
                            <Label className="label-overline mb-2 block">Nombre visible</Label>
                            <Input value={newCollection.display_name} onChange={(e) => setNewCollection({ ...newCollection, display_name: e.target.value })} className="input-industrial" placeholder="Mi Tabla" />
                        </div>
                        <div>
                            <Label className="label-overline mb-2 block">Descripción</Label>
                            <Textarea value={newCollection.description} onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })} className="input-industrial" />
                        </div>
                        
                        {/* Menu configuration */}
                        <div className="border-t border-[#E5E5E5] pt-4 mt-4">
                            <h4 className="font-semibold text-sm mb-3">Configuración del menú lateral</h4>
                            <div className="flex items-center gap-2 mb-3">
                                <input 
                                    type="checkbox" 
                                    id="show_in_menu"
                                    checked={newCollection.show_in_menu} 
                                    onChange={(e) => setNewCollection({ ...newCollection, show_in_menu: e.target.checked })}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="show_in_menu" className="text-sm">Mostrar en menú lateral</Label>
                            </div>
                            
                            {newCollection.show_in_menu && (
                                <>
                                    <div className="mb-3">
                                        <Label className="label-overline mb-2 block">Nombre en menú</Label>
                                        <Input 
                                            value={newCollection.menu_name} 
                                            onChange={(e) => setNewCollection({ ...newCollection, menu_name: e.target.value })} 
                                            className="input-industrial" 
                                            placeholder="Nombre a mostrar en el menú"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="label-overline mb-2 block">Icono</Label>
                                            <Select 
                                                value={newCollection.menu_icon} 
                                                onValueChange={(v) => setNewCollection({ ...newCollection, menu_icon: v })}
                                            >
                                                <SelectTrigger className="input-industrial">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="database">Base de datos</SelectItem>
                                                    <SelectItem value="warehouse">Almacén</SelectItem>
                                                    <SelectItem value="truck">Vehículo</SelectItem>
                                                    <SelectItem value="hardhat">Casco</SelectItem>
                                                    <SelectItem value="listchecks">Tareas</SelectItem>
                                                    <SelectItem value="wrench">Herramienta</SelectItem>
                                                    <SelectItem value="package">Paquete</SelectItem>
                                                    <SelectItem value="users">Usuarios</SelectItem>
                                                    <SelectItem value="calendar">Calendario</SelectItem>
                                                    <SelectItem value="clipboard">Portapapeles</SelectItem>
                                                    <SelectItem value="folder">Carpeta</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="label-overline mb-2 block">Orden</Label>
                                            <Input 
                                                type="number" 
                                                value={newCollection.menu_order} 
                                                onChange={(e) => setNewCollection({ ...newCollection, menu_order: parseInt(e.target.value) || 99 })} 
                                                className="input-industrial"
                                                min="1"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={() => setNewCollectionOpen(false)} className="flex-1">Cancelar</Button>
                            <Button onClick={handleCreateCollection} className="btn-primary flex-1">Crear</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Data Dialog */}
            <Dialog open={dataDialogOpen} onOpenChange={setDataDialogOpen}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingData ? "Editar registro" : "Nuevo registro"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {selectedCollection?.fields?.map(field => (
                            <div key={field.name}>
                                <Label className="label-overline mb-2 block">{field.label || field.name} {field.required && <span className="text-red-500">*</span>}</Label>
                                {field.type === "select" ? (
                                    <Select value={dataForm[field.name] || ""} onValueChange={(v) => setDataForm({ ...dataForm, [field.name]: v })}>
                                        <SelectTrigger className="input-industrial"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                                        <SelectContent>
                                            {field.options?.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                ) : field.type === "text" ? (
                                    <Textarea value={dataForm[field.name] || ""} onChange={(e) => setDataForm({ ...dataForm, [field.name]: e.target.value })} className="input-industrial" />
                                ) : field.type === "number" ? (
                                    <Input type="number" value={dataForm[field.name] || ""} onChange={(e) => setDataForm({ ...dataForm, [field.name]: parseFloat(e.target.value) || 0 })} className="input-industrial" />
                                ) : field.type === "date" ? (
                                    <Input type="date" value={dataForm[field.name] || ""} onChange={(e) => setDataForm({ ...dataForm, [field.name]: e.target.value })} className="input-industrial" />
                                ) : field.type === "boolean" ? (
                                    <Select value={String(dataForm[field.name] || false)} onValueChange={(v) => setDataForm({ ...dataForm, [field.name]: v === "true" })}>
                                        <SelectTrigger className="input-industrial"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="true">Sí</SelectItem>
                                            <SelectItem value="false">No</SelectItem>
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input value={dataForm[field.name] || ""} onChange={(e) => setDataForm({ ...dataForm, [field.name]: e.target.value })} className="input-industrial" />
                                )}
                            </div>
                        ))}
                        <div className="flex gap-3 pt-4">
                            <Button variant="outline" onClick={() => setDataDialogOpen(false)} className="flex-1">Cancelar</Button>
                            <Button onClick={editingData ? handleUpdateData : handleCreateData} className="btn-primary flex-1">
                                {editingData ? "Guardar" : "Crear"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* New User Dialog */}
            <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Nuevo Usuario</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="label-overline mb-2 block">Nombre *</Label>
                            <Input 
                                value={newUser.name} 
                                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} 
                                className="input-industrial" 
                                placeholder="Nombre completo"
                            />
                        </div>
                        <div>
                            <Label className="label-overline mb-2 block">Email *</Label>
                            <Input 
                                type="email"
                                value={newUser.email} 
                                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} 
                                className="input-industrial" 
                                placeholder="email@ejemplo.com"
                            />
                        </div>
                        <div>
                            <Label className="label-overline mb-2 block">Contraseña *</Label>
                            <Input 
                                type="password"
                                value={newUser.password} 
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} 
                                className="input-industrial" 
                                placeholder="Contraseña"
                            />
                        </div>
                        <div>
                            <Label className="label-overline mb-2 block">Empresa</Label>
                            <Input 
                                value={newUser.company} 
                                onChange={(e) => setNewUser({ ...newUser, company: e.target.value })} 
                                className="input-industrial" 
                                placeholder="Nombre de la empresa"
                            />
                        </div>
                        <div>
                            <Label className="label-overline mb-2 block">Rol</Label>
                            <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                                <SelectTrigger className="input-industrial"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">Usuario</SelectItem>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex gap-3 pt-4">
                            <Button variant="outline" onClick={() => setNewUserOpen(false)} className="flex-1">Cancelar</Button>
                            <Button onClick={handleCreateUser} className="btn-primary flex-1">Crear Usuario</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Editar Usuario</DialogTitle>
                    </DialogHeader>
                    {editingUser && (
                        <div className="space-y-4 mt-4">
                            <div>
                                <Label className="label-overline mb-2 block">Nombre</Label>
                                <Input value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} className="input-industrial" />
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">Empresa</Label>
                                <Input value={editingUser.company || ""} onChange={(e) => setEditingUser({ ...editingUser, company: e.target.value })} className="input-industrial" />
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">Rol</Label>
                                <Select value={editingUser.role} onValueChange={(v) => setEditingUser({ ...editingUser, role: v })}>
                                    <SelectTrigger className="input-industrial"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="user">Usuario</SelectItem>
                                        <SelectItem value="admin">Administrador</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex gap-3">
                                <Button variant="outline" onClick={() => setEditUserOpen(false)} className="flex-1">Cancelar</Button>
                                <Button onClick={handleUpdateUser} className="btn-primary flex-1">Guardar</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminPage;
